import SavePaymentData from "../../logic/payment/SavePayment";
import { DOWNLOAD_CODE_REGEX } from "@helpers/regexPatterns";

export default {
    mixins: [],
    data: () => ({}),
    computed: {

    },
    methods: {
        /**
         * Hides the PayPal error modal after the user acknowledges the message.
         */
        closeModalPaypalError() {
            document.getElementById('modalPaypalError').classList.add("hidden");
        },
        /**
         * Hides the generic sale error modal.
         */
        closeModalSaleError() {
            document.getElementById('modalSaleError').classList.add("hidden");
        },
        /**
         * Gets the buyer email used during PayPal payment confirmation.
         * The form email has priority and the store email is used as fallback.
         *
         * @returns {string} Buyer email without leading or trailing spaces.
         */
        getPaypalBuyerEmail() {
            return (this.Buyer?.Email || this.$store.state.email || "").trim();
        },
        /**
         * Shows the PayPal error modal and fills the transaction information
         * returned by PayPal or generated by the booking flow.
         *
         * @param {string} status Error status to display.
         * @param {string} transactionId PayPal order id or merchant order id.
         */
        showPaypalErrorModal(status = "ERROR", transactionId = "") {
            const successModal = document.getElementById('modalPaypalSuccess');
            const errorModal = document.getElementById('modalPaypalError');
            const transactionStatus = document.getElementById('paypal_error_transaction_status');
            const transactionIdentifier = document.getElementById('paypal_error_transaction_id');

            if (transactionStatus) transactionStatus.innerText = status;
            if (transactionIdentifier) transactionIdentifier.innerText = transactionId;
            if (successModal) successModal.classList.add("hidden");
            if (errorModal) errorModal.classList.remove("hidden");
        },
        /**
         * Redirects the customer to the payment success page after the sale is saved.
         *
         * @param {string} dsSaleID External sale identifier returned by SIVEX.
         */
        redirectPaypalSuccess(dsSaleID) {
            const route = this.$router.resolve({
                name: "success_payment",
                params: {
                    dsSalesId: dsSaleID,
                },
            });
        
            location.href = `${window.location.origin}${route.href}`;
        },
        /**
         * Validates the format accepted by ApiKoh for a download code.
         *
         * @param {*} downloadCode Candidate value.
         * @returns {boolean} True when the value is a valid download code.
         */
        isValidDownloadCode(downloadCode) {
            return DOWNLOAD_CODE_REGEX.test(String(downloadCode || "").trim());
        },
        /**
         * Gets the PayPal video download code from the canonical backend node.
         * SavePayment exposes the generated code only in save.data.downloadCode;
         * any other downloadCode node is ignored to avoid duplicated contracts.
         *
         * @param {object|null} response SavePayment response.
         * @returns {string|null} Download code found in save.data.downloadCode.
         */
        getSavePaymentDownloadCode(response = null) {
            const downloadCode = response?.save?.data?.downloadCode;

            return this.isValidDownloadCode(downloadCode)
                ? String(downloadCode).trim()
                : null;
        },
        /**
         * Stores the download code that the success page must display.
         * Existing download-code purchases keep the original code. Video-only
         * purchases keep the code returned after SavePayment so the success page
         * can show it even when RecoverySale does not expose it as DownloadCode.
         *
         * @param {object|null} response SavePayment response.
         */
        persistOriginalDownloadCode(response = null) {
            sessionStorage.removeItem('originalDownloadCode');

            const cartItems = Object.values(this.$store.state.shoppingCart || {});
            const type0Items = cartItems.filter((item) => Number(item.type) === 0);
            const lastType0Code = type0Items.length ? type0Items[type0Items.length - 1].code : null;

            if (lastType0Code) {
                sessionStorage.setItem('originalDownloadCode', lastType0Code);
                return;
            }

            const isOnlyVideoSale = cartItems.length > 0 && cartItems.every((item) => Number(item.type) === 3);

            if (!isOnlyVideoSale) {
                return;
            }

            const downloadCode = this.getSavePaymentDownloadCode(response);

            if (downloadCode) {
                sessionStorage.setItem('originalDownloadCode', downloadCode);
            }
        },
        /**
         * Gets the event-sale details required by SavePayment.
         * When the cart does not contain an event item, the method returns the
         * default non-event structure expected by the backend.
         *
         * @returns {{isEvent: boolean, agentDetails: Object}} Event payment data.
         */
        getPaypalEventDetails() {
            const shoppingCartItems = Object.values(this.$store.state.shoppingCart || {});
            const eventItem = shoppingCartItems.find((item) => item?.isEvent === true);

            return {
                isEvent: eventItem?.isEvent === true,
                agentDetails: eventItem?.eventDetails?.AgentDetails || {},
            };
        },
        /**
         * Gets the approved sale status returned by the backend after PayPal confirmation.
         * The frontend contract is saleStatus, but statusSivex is accepted as a legacy fallback
         * while all environments finish aligning the SavePayment response.
         *
         * @param {object|null} response SavePayment response.
         * @returns {number|null} Sale status returned by the backend.
         */
        getPaypalSaleStatus(response = null) {
            const status = response?.saleStatus ?? response?.SaleStatus ?? response?.statusSivex ?? null;

            return status !== null && status !== undefined && status !== "" ? Number(status) : null;
        },
        /**
         * Creates the PayPal order, confirms the payment in SIVEX and stores the sale in ApiKoh.
         * Type: 1 Paypal.
         */
        async PayPalButtons() {
            let parameters = {};

            try {
                const paypalEventDetails = this.getPaypalEventDetails();

                parameters.ShoppingCart = Object.values(this.$store.state.shoppingCart || {});
                parameters.email = this.getPaypalBuyerEmail();
                parameters.country = this.$store.state.Currency;
                parameters.isEvent = paypalEventDetails.isEvent;
                parameters.agentDetails = paypalEventDetails.agentDetails;

                paypal.Buttons({
                    createOrder: () => {
                        return this.Booking()
                            .then((response) => {
                                const merchantOrderId = response?.merchantOrderId;

                                if (!merchantOrderId) {
                                    throw new Error("Missing PayPal merchant order id");
                                }

                                parameters.salesId = response.salesId;
                                parameters.dsSalesId = response.dsSalesId;
                                parameters.merchantOrderId = merchantOrderId;
                                parameters.email = this.getPaypalBuyerEmail();

                                return merchantOrderId;
                            })
                            .catch((error) => {
                                Vue.$toast.open({
                                    message: this.$t("paypal.create_order_error"),
                                    type: "error",
                                });

                                throw error;
                            });
                    },
                    onInit: (data, actions) => {
                        actions.disable();

                        const checkReadPaypal = document.querySelector("#checkReadPaypal");
                        if (!checkReadPaypal) return;

                        checkReadPaypal.addEventListener("change", function (event) {
                            if (event.target.checked) {
                                actions.enable();
                            } else {
                                actions.disable();
                            }
                        });
                    },
                    onApprove: async (data) => {
                        const successModal = document.getElementById('modalPaypalSuccess');
                        if (successModal) successModal.classList.remove("hidden");

                        try {
                            parameters.email = this.getPaypalBuyerEmail();
                            const response = await SavePaymentData.post(parameters);
                            const isApproved = this.getPaypalSaleStatus(response) === 7;

                            if (response?.save?.apiKoh) {
                                sessionStorage.setItem("apiKoh", JSON.stringify(response.save.apiKoh));
                            }

                            if (!isApproved) {
                                this.showPaypalErrorModal("PAYMENT_NOT_CONFIRMED", data?.orderID || parameters.merchantOrderId || "");

                                Vue.$toast.open({
                                    message: this.$t("messages.error_sale_made"),
                                    type: "error",
                                });

                                return;
                            }

                            this.persistOriginalDownloadCode(response);
                            SavePaymentData.clearShoppingCart();
                            this.redirectPaypalSuccess(response.dsSaleID);
                        } catch (error) {
                            this.showPaypalErrorModal("PAYMENT_SAVE_ERROR", data?.orderID || parameters.merchantOrderId || "");
                        }
                    },
                    onError: () => {
                        this.showPaypalErrorModal("PAYPAL_ORDER_ERROR", parameters.merchantOrderId || "");
                    },
                })
                    .render("#paypal-button-container");
            } catch (error) {
                this.showPaypalErrorModal("PAYPAL_BUTTON_ERROR", parameters?.merchantOrderId || "");
            }
        }
    }
};